import {
  FleetAnalyticsV1Schema,
  AnalyticsSummaryV1Schema,
  PRODUCT_KEYS,
  type AnalyticsDaysV1,
  type AnalyticsProductFilterV1,
  type AnalyticsSummaryV1,
  type AnalyticsTotalsV1,
  type FleetAnalyticsOrganizationV1,
  type FleetAnalyticsV1,
} from "@fulcrum5/suite-contracts";
import { RotateCw } from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type MouseEvent,
} from "react";
import {
  navigate,
  PLATFORM_NAVIGATE_EVENT,
} from "../app/navigation.ts";
import { PlatformApiError } from "../api/platformApi.ts";
import { usePlatformSession } from "../session/PlatformSessionProvider.tsx";
import {
  costCoverageLabel,
  formatAnalyticsDuration,
  formatAnalyticsInteger,
  formatAnalyticsPercent,
  formatAnalyticsTokens,
  formatAnalyticsUsdMicros,
} from "./analyticsFormat.ts";
import { useOrganizationResource } from "./useOrganizationResource.ts";
import "./dashboardPages.css";

const ANALYTICS_DAYS: readonly AnalyticsDaysV1[] = [7, 30, 90, 365];
const UI_PRODUCT_FILTERS = ["all", "platform", ...PRODUCT_KEYS] as const;
type UiProductFilter = (typeof UI_PRODUCT_FILTERS)[number];

interface AnalyticsUrlState {
  readonly days: AnalyticsDaysV1;
  readonly product: UiProductFilter;
  readonly organizationId: string | null;
}

const ANALYTICS_FLEET_PAGE_LIMIT = 100;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function analyticsSearchSnapshot(): string {
  return typeof window === "undefined" ? "" : window.location.search;
}

function subscribeAnalyticsSearch(onStoreChange: () => void): () => void {
  if (typeof window === "undefined") return () => undefined;
  window.addEventListener("popstate", onStoreChange);
  window.addEventListener(PLATFORM_NAVIGATE_EVENT, onStoreChange);
  return () => {
    window.removeEventListener("popstate", onStoreChange);
    window.removeEventListener(PLATFORM_NAVIGATE_EVENT, onStoreChange);
  };
}

function isAnalyticsDay(value: string | null): value is `${AnalyticsDaysV1}` {
  return value === "7" || value === "30" || value === "90" || value === "365";
}

function isUiProductFilter(value: string | null): value is UiProductFilter {
  return value !== null && UI_PRODUCT_FILTERS.includes(value as UiProductFilter);
}

function normalizedOrganizationId(value: string | null): string | null {
  return value !== null && UUID_PATTERN.test(value) ? value.toLowerCase() : null;
}

function analyticsUrlState(search: string): AnalyticsUrlState {
  const params = new URLSearchParams(search);
  const rawDays = params.get("days");
  const rawProduct = params.get("product");
  return {
    days: isAnalyticsDay(rawDays) ? Number(rawDays) as AnalyticsDaysV1 : 30,
    product: isUiProductFilter(rawProduct) ? rawProduct : "all",
    organizationId: normalizedOrganizationId(params.get("organization")),
  };
}

function analyticsPath(state: AnalyticsUrlState): string {
  const params = new URLSearchParams({
    days: String(state.days),
    product: state.product,
  });
  if (state.organizationId !== null) params.set("organization", state.organizationId);
  return `/analytics?${params.toString()}`;
}

function shouldHandleAnalyticsLink(event: MouseEvent<HTMLAnchorElement>): boolean {
  return !(
    event.defaultPrevented
    || event.button !== 0
    || event.metaKey
    || event.altKey
    || event.ctrlKey
    || event.shiftKey
  );
}

function organizationAnalyticsPath(organizationId: string, state: AnalyticsUrlState): string {
  return `/api/organizations/${organizationId}/analytics?days=${state.days}&product=${state.product}`;
}

function fleetAnalyticsPath(state: AnalyticsUrlState, cursor: string | null): string {
  const params = new URLSearchParams({
    days: String(state.days),
    product: state.product,
  });
  if (cursor !== null) params.set("cursor", cursor);
  params.set("limit", String(ANALYTICS_FLEET_PAGE_LIMIT));
  return `/api/admin/analytics?${params.toString()}`;
}

function f5OrganizationAnalyticsPath(organizationId: string, state: AnalyticsUrlState): string {
  return `/api/admin/analytics/organizations/${organizationId}?days=${state.days}&product=${state.product}`;
}

function useAnalyticsUrlState(): readonly [AnalyticsUrlState, (next: AnalyticsUrlState) => void] {
  const search = useSyncExternalStore(subscribeAnalyticsSearch, analyticsSearchSnapshot, () => "");
  const state = useMemo(() => analyticsUrlState(search), [search]);
  const setState = useCallback((next: AnalyticsUrlState) => {
    navigate(analyticsPath(next));
  }, []);
  return [state, setState] as const;
}

function AnalyticsFilters({
  state,
  onChange,
}: {
  readonly state: AnalyticsUrlState;
  readonly onChange: (next: AnalyticsUrlState) => void;
}): JSX.Element {
  return (
    <div className="analytics-page__filters" aria-label="Analytics filters">
      <div className="analytics-page__ranges" aria-label="Analytics range" role="group">
        {ANALYTICS_DAYS.map((days) => (
          <button
            aria-pressed={state.days === days}
            className="analytics-page__range-button"
            key={days}
            onClick={() => onChange({ ...state, days })}
            type="button"
          >
            {days} days
          </button>
        ))}
      </div>
      <label className="platform-field analytics-page__product-field" htmlFor="analytics-product-filter">
        <span>Product</span>
        <select
          id="analytics-product-filter"
          onChange={(event) => {
            const product = event.target.value;
            if (isUiProductFilter(product)) onChange({ ...state, product });
          }}
          value={state.product}
        >
          <option value="all">All products</option>
          <option value="platform">Platform</option>
          <option value="codify">Codify</option>
          <option value="source">Source</option>
          <option value="radius">Radius</option>
          <option value="function">Function</option>
        </select>
      </label>
    </div>
  );
}

function MetricGrid({
  totals,
  canBeHidden,
}: {
  readonly totals: AnalyticsTotalsV1;
  readonly canBeHidden: boolean;
}): JSX.Element {
  const totalTokens = totals.inputTokens + totals.outputTokens + totals.cachedTokens;
  const cells = [
    ["Active members", formatAnalyticsInteger(totals.activeMembers)],
    ["Product opens", formatAnalyticsInteger(totals.productOpens)],
    ["Product sessions", formatAnalyticsInteger(totals.productSessions)],
    ["Runs", formatAnalyticsInteger(totals.runs)],
    ["Success rate", formatAnalyticsPercent(totals.successRate)],
    ["P50 duration", formatAnalyticsDuration(totals.p50DurationMs)],
    ["P95 duration", formatAnalyticsDuration(totals.p95DurationMs)],
    ["Tokens", formatAnalyticsTokens(totalTokens)],
    ["Estimated cost", formatAnalyticsUsdMicros(totals.estimatedCostMicros)],
  ] as const;
  return (
    <section aria-labelledby="analytics-metrics-title" className="analytics-page__metrics">
      <div className="dashboard-section-heading">
        <div>
          <p className="dashboard-kicker">Range summary</p>
          <h2 id="analytics-metrics-title">Operational health</h2>
        </div>
        <span className="analytics-page__coverage" data-coverage={totals.costCoverage}>
          {costCoverageLabel(totals.costCoverage, canBeHidden && totals.estimatedCostMicros === null)}
        </span>
      </div>
      <dl className="analytics-page__metric-grid">
        {cells.map(([label, value]) => (
          <div className="analytics-page__metric" key={label}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function DailyActivity({ daily }: { readonly daily: AnalyticsSummaryV1["daily"] }): JSX.Element {
  if (daily.length === 0) {
    return (
      <section aria-labelledby="analytics-daily-title" className="analytics-page__section analytics-page__section--empty">
        <div className="dashboard-section-heading">
          <div>
            <p className="dashboard-kicker">Activity</p>
            <h2 id="analytics-daily-title">Daily activity</h2>
          </div>
        </div>
        <p className="analytics-page__empty-message">No activity in this range.</p>
      </section>
    );
  }

  const maximum = Math.max(1, ...daily.flatMap((entry) => [entry.productOpens, entry.runs]));
  const step = 640 / daily.length;
  const barWidth = Math.max(1, step * 0.32);
  return (
    <section aria-labelledby="analytics-daily-title" className="analytics-page__section">
      <div className="dashboard-section-heading">
        <div>
          <p className="dashboard-kicker">Activity</p>
          <h2 id="analytics-daily-title">Daily activity</h2>
        </div>
      </div>
      <div className="analytics-page__activity-layout">
        <div className="analytics-page__chart-wrap">
          <svg aria-labelledby="analytics-daily-chart-title" className="analytics-page__chart" role="img" viewBox="0 0 640 190">
            <title id="analytics-daily-chart-title">Daily product opens and runs</title>
            <line className="analytics-page__chart-baseline" x1="0" x2="640" y1="170" y2="170" />
            {daily.map((entry, index) => {
              const x = index * step;
              const opensHeight = (entry.productOpens / maximum) * 150;
              const runsHeight = (entry.runs / maximum) * 150;
              return (
                <g key={entry.date}>
                  <rect className="analytics-page__chart-bar analytics-page__chart-bar--opens" height={opensHeight} rx="1" width={barWidth} x={x + (step * 0.12)} y={170 - opensHeight} />
                  <rect className="analytics-page__chart-bar analytics-page__chart-bar--runs" height={runsHeight} rx="1" width={barWidth} x={x + (step * 0.56)} y={170 - runsHeight} />
                </g>
              );
            })}
          </svg>
          <div aria-hidden="true" className="analytics-page__chart-key">
            <span><i className="analytics-page__chart-swatch analytics-page__chart-swatch--opens" />Product opens</span>
            <span><i className="analytics-page__chart-swatch analytics-page__chart-swatch--runs" />Runs</span>
          </div>
        </div>
        <div aria-label="Daily activity data" className="analytics-page__table-wrap" role="region" tabIndex={0}>
          <table aria-label="Daily activity data" className="analytics-page__table">
            <thead>
              <tr><th>Date</th><th>Opens</th><th>Runs</th><th>Successful</th><th>Cost</th></tr>
            </thead>
            <tbody>
              {daily.map((entry) => (
                <tr key={entry.date}>
                  <th scope="row">{entry.date}</th>
                  <td>{formatAnalyticsInteger(entry.productOpens)}</td>
                  <td>{formatAnalyticsInteger(entry.runs)}</td>
                  <td>{formatAnalyticsInteger(entry.successfulRuns)}</td>
                  <td>{formatAnalyticsUsdMicros(entry.estimatedCostMicros)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function ProductActivity({ products }: { readonly products: AnalyticsSummaryV1["products"] }): JSX.Element {
  return (
    <section aria-labelledby="analytics-products-title" className="analytics-page__section">
      <div className="dashboard-section-heading">
        <div>
          <p className="dashboard-kicker">Products</p>
          <h2 id="analytics-products-title">Product activity</h2>
        </div>
      </div>
      {products.length === 0 ? <p className="analytics-page__empty-message">No product activity in this range.</p> : (
        <div aria-label="Product activity data" className="analytics-page__table-wrap" role="region" tabIndex={0}>
          <table aria-label="Product activity" className="analytics-page__table">
            <thead>
              <tr><th>Product</th><th>Opens</th><th>Sessions</th><th>Runs</th><th>Success rate</th><th>Cost</th></tr>
            </thead>
            <tbody>
              {products.map((entry) => (
                <tr key={entry.product}>
                  <th scope="row">{entry.product}</th>
                  <td>{formatAnalyticsInteger(entry.opens)}</td>
                  <td>{formatAnalyticsInteger(entry.sessions)}</td>
                  <td>{formatAnalyticsInteger(entry.runs)}</td>
                  <td>{formatAnalyticsPercent(entry.successRate)}</td>
                  <td>{formatAnalyticsUsdMicros(entry.estimatedCostMicros)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

type AnalyticsResourceStatus = "idle" | "loading" | "ready" | "error";

interface FleetResourceState {
  readonly status: AnalyticsResourceStatus;
  readonly data: FleetAnalyticsV1 | null;
  readonly organizations: readonly FleetAnalyticsOrganizationV1[];
  readonly nextCursor: string | null;
  readonly message: string | null;
  readonly canRetry: boolean;
  readonly isLoadingMore: boolean;
}

interface FleetAnalyticsResource extends FleetResourceState {
  reload(): Promise<void>;
  loadMore(): Promise<void>;
}

interface F5OrganizationResource {
  readonly status: AnalyticsResourceStatus;
  readonly data: AnalyticsSummaryV1 | null;
  readonly message: string | null;
  readonly canRetry: boolean;
  reload(): Promise<void>;
}

const EMPTY_FLEET_RESOURCE: FleetResourceState = {
  status: "idle",
  data: null,
  organizations: [],
  nextCursor: null,
  message: null,
  canRetry: false,
  isLoadingMore: false,
};

const EMPTY_F5_ORGANIZATION_RESOURCE: Omit<F5OrganizationResource, "reload"> = {
  status: "idle",
  data: null,
  message: null,
  canRetry: false,
};

function analyticsErrorDetails(error: unknown): Pick<FleetResourceState, "message" | "canRetry"> {
  if (error instanceof PlatformApiError) {
    return {
      message: error.message,
      canRetry: error.retryable || error.status === 409 || error.status >= 500,
    };
  }
  return { message: "Analytics could not be loaded.", canRetry: true };
}

function appendFleetOrganizations(
  current: readonly FleetAnalyticsOrganizationV1[],
  incoming: readonly FleetAnalyticsOrganizationV1[],
): readonly FleetAnalyticsOrganizationV1[] {
  const existing = new Set(current.map((organization) => organization.organizationId));
  return [...current, ...incoming.filter((organization) => !existing.has(organization.organizationId))];
}

/** Keeps opaque fleet cursor state out of browser URLs while guarding refresh races. */
function useFleetAnalyticsResource(query: AnalyticsUrlState): FleetAnalyticsResource {
  const session = usePlatformSession();
  const [resource, setResource] = useState<FleetResourceState>(EMPTY_FLEET_RESOURCE);
  const requestVersion = useRef(0);
  const requestController = useRef<AbortController | null>(null);

  const requestPage = useCallback(async (cursor: string | null, append: boolean) => {
    if (session.phase !== "ready" || session.identity?.isF5Admin !== true) {
      requestVersion.current += 1;
      requestController.current?.abort();
      requestController.current = null;
      setResource(EMPTY_FLEET_RESOURCE);
      return;
    }

    requestController.current?.abort();
    const controller = new AbortController();
    requestController.current = controller;
    const version = requestVersion.current + 1;
    requestVersion.current = version;
    setResource((current) => append
      ? { ...current, message: null, canRetry: false, isLoadingMore: true }
      : { ...EMPTY_FLEET_RESOURCE, status: "loading" });

    try {
      const result = await session.api.get({
        path: fleetAnalyticsPath(query, cursor),
        responseSchema: FleetAnalyticsV1Schema,
        signal: controller.signal,
      });
      if (requestVersion.current !== version || controller.signal.aborted) return;
      setResource((current) => ({
        status: "ready",
        data: result.data,
        organizations: append
          ? appendFleetOrganizations(current.organizations, result.data.organizations)
          : result.data.organizations,
        nextCursor: result.data.nextCursor,
        message: null,
        canRetry: false,
        isLoadingMore: false,
      }));
    } catch (error) {
      if (requestVersion.current !== version || controller.signal.aborted) return;
      setResource((current) => ({
        ...current,
        status: "error",
        ...analyticsErrorDetails(error),
        isLoadingMore: false,
      }));
    } finally {
      if (requestController.current === controller) requestController.current = null;
    }
  }, [query, session.api, session.identity?.isF5Admin, session.phase]);

  const reload = useCallback(async () => {
    await requestPage(null, false);
  }, [requestPage]);

  const loadMore = useCallback(async () => {
    if (resource.nextCursor === null || resource.status === "loading" || resource.isLoadingMore) return;
    await requestPage(resource.nextCursor, true);
  }, [requestPage, resource.isLoadingMore, resource.nextCursor, resource.status]);

  useEffect(() => {
    void reload();
    return () => {
      requestVersion.current += 1;
      requestController.current?.abort();
      requestController.current = null;
    };
  }, [reload]);

  return { ...resource, reload, loadMore };
}

/** F5 drill-down requests are impossible until a fleet row has approved the id. */
function useF5OrganizationAnalyticsResource(
  organizationId: string | null,
  query: AnalyticsUrlState,
): F5OrganizationResource {
  const session = usePlatformSession();
  const [resource, setResource] = useState<Omit<F5OrganizationResource, "reload">>(EMPTY_F5_ORGANIZATION_RESOURCE);
  const requestVersion = useRef(0);
  const requestController = useRef<AbortController | null>(null);
  const contextKey = useRef<string | null>(null);

  const reload = useCallback(async () => {
    if (session.phase !== "ready" || session.identity?.isF5Admin !== true || organizationId === null) {
      requestVersion.current += 1;
      requestController.current?.abort();
      requestController.current = null;
      contextKey.current = null;
      setResource(EMPTY_F5_ORGANIZATION_RESOURCE);
      return;
    }

    const nextContextKey = [
      session.identity.platformSessionId,
      organizationId,
      query.days,
      query.product,
    ].join(":");
    const contextChanged = contextKey.current !== nextContextKey;
    contextKey.current = nextContextKey;
    requestController.current?.abort();
    const controller = new AbortController();
    requestController.current = controller;
    const version = requestVersion.current + 1;
    requestVersion.current = version;
    setResource((current) => ({
      status: "loading",
      data: contextChanged ? null : current.data,
      message: null,
      canRetry: false,
    }));

    try {
      const result = await session.api.get({
        path: f5OrganizationAnalyticsPath(organizationId, query),
        responseSchema: AnalyticsSummaryV1Schema,
        signal: controller.signal,
      });
      if (requestVersion.current !== version || controller.signal.aborted) return;
      setResource({ status: "ready", data: result.data, message: null, canRetry: false });
    } catch (error) {
      if (requestVersion.current !== version || controller.signal.aborted) return;
      setResource((current) => ({
        ...current,
        status: "error",
        ...analyticsErrorDetails(error),
      }));
    } finally {
      if (requestController.current === controller) requestController.current = null;
    }
  }, [organizationId, query, session.api, session.identity?.isF5Admin, session.phase]);

  useEffect(() => {
    void reload();
    return () => {
      requestVersion.current += 1;
      requestController.current?.abort();
      requestController.current = null;
    };
  }, [reload]);

  return { ...resource, reload };
}

function OrganizationAnalyticsPage(): JSX.Element {
  const session = usePlatformSession();
  const [state, setState] = useAnalyticsUrlState();
  const path = useCallback(
    (organizationId: string) => organizationAnalyticsPath(organizationId, state),
    [state],
  );
  const resource = useOrganizationResource({
    path,
    responseSchema: AnalyticsSummaryV1Schema,
    contextKey: `${state.days}:${state.product}`,
  });
  const retry = useCallback(() => { void resource.reload(); }, [resource]);
  const canBeHidden = session.identity?.organizationRole !== "owner";

  if (resource.status === "loading" && !resource.data) {
    return <section className="platform-page dashboard-page"><p className="platform-loading" role="status">Loading analytics.</p></section>;
  }
  if (!resource.data && resource.status === "idle") {
    return <section className="platform-page dashboard-page"><p className="platform-empty-state" role="status">An active organization is required to view analytics.</p></section>;
  }
  if (!resource.data) {
    return (
      <section className="platform-page dashboard-page">
        <div className="platform-notice platform-notice--error" role="alert">
          <p>{resource.message ?? "Analytics could not be loaded."}</p>
          {resource.canRetry ? <button className="platform-button platform-button--secondary" onClick={retry} type="button">Retry analytics</button> : null}
        </div>
      </section>
    );
  }

  return (
    <section aria-labelledby="analytics-page-title" className="platform-page dashboard-page analytics-page">
      <header className="dashboard-page__header">
        <div>
          <p className="platform-page__eyebrow">Workspace</p>
          <h1 id="analytics-page-title">Analytics</h1>
        </div>
        <span className="platform-badge" data-tone="active">Organization analytics</span>
      </header>
      <AnalyticsFilters onChange={setState} state={state} />
      {resource.status === "error" ? (
        <div className="platform-notice platform-notice--error dashboard-page__notice" role="alert">
          <p>{resource.message ?? "Analytics could not be refreshed."}</p>
          {resource.canRetry ? <button className="platform-button platform-button--secondary" onClick={retry} type="button">Retry analytics</button> : null}
        </div>
      ) : null}
      <MetricGrid canBeHidden={canBeHidden} totals={resource.data.totals} />
      <DailyActivity daily={resource.data.daily} />
      <ProductActivity products={resource.data.products} />
      {resource.status === "loading" ? <p className="dashboard-page__refreshing" role="status"><RotateCw aria-hidden="true" className="platform-spin" size={15} /> Refreshing analytics.</p> : null}
    </section>
  );
}

function FleetOrganizationTable({
  organizations,
  state,
  onSelect,
  onLoadMore,
  hasMore,
  isLoadingMore,
}: {
  readonly organizations: readonly FleetAnalyticsOrganizationV1[];
  readonly state: AnalyticsUrlState;
  readonly onSelect: (organizationId: string) => void;
  readonly onLoadMore: () => void;
  readonly hasMore: boolean;
  readonly isLoadingMore: boolean;
}): JSX.Element {
  return (
    <section aria-labelledby="analytics-fleet-organizations-title" className="analytics-page__section">
      <div className="dashboard-section-heading">
        <div>
          <p className="dashboard-kicker">Fleet coverage</p>
          <h2 id="analytics-fleet-organizations-title">Organization aggregates</h2>
        </div>
      </div>
      {organizations.length === 0 ? <p className="analytics-page__empty-message">No organizations have activity in this range.</p> : (
        <div className="analytics-page__table-wrap analytics-page__table-wrap--fleet">
          <table aria-label="Fleet organization aggregates" className="analytics-page__table analytics-page__table--fleet">
            <thead>
              <tr><th>Organization</th><th>Active</th><th>Opens</th><th>Runs</th><th>Success rate</th><th>Estimated cost</th></tr>
            </thead>
            <tbody>
              {organizations.map((organization) => {
                const href = analyticsPath({ ...state, organizationId: organization.organizationId });
                return (
                  <tr key={organization.organizationId}>
                    <th scope="row">
                      <a
                        className="analytics-page__organization-link"
                        href={href}
                        onClick={(event) => {
                          if (!shouldHandleAnalyticsLink(event)) return;
                          event.preventDefault();
                          onSelect(organization.organizationId);
                        }}
                      >
                        {organization.organizationName}
                      </a>
                    </th>
                    <td>{formatAnalyticsInteger(organization.totals.activeMembers)}</td>
                    <td>{formatAnalyticsInteger(organization.totals.productOpens)}</td>
                    <td>{formatAnalyticsInteger(organization.totals.runs)}</td>
                    <td>{formatAnalyticsPercent(organization.totals.successRate)}</td>
                    <td>{formatAnalyticsUsdMicros(organization.totals.estimatedCostMicros)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {hasMore ? (
        <div className="analytics-page__load-more">
          <button
            className="platform-button platform-button--secondary"
            disabled={isLoadingMore}
            onClick={onLoadMore}
            type="button"
          >
            {isLoadingMore ? "Loading organizations" : "Load more organizations"}
          </button>
        </div>
      ) : null}
    </section>
  );
}

function F5OrganizationDrilldown({
  organization,
  resource,
  onReturnToFleet,
}: {
  readonly organization: FleetAnalyticsOrganizationV1;
  readonly resource: F5OrganizationResource;
  readonly onReturnToFleet: () => void;
}): JSX.Element {
  const retry = useCallback(() => { void resource.reload(); }, [resource]);
  return (
    <div className="analytics-page__drilldown">
      <div className="analytics-page__drilldown-header">
        <div>
          <p className="dashboard-kicker">Organization aggregate</p>
          <h2>{organization.organizationName} analytics</h2>
        </div>
        <button className="platform-button platform-button--quiet" onClick={onReturnToFleet} type="button">Back to fleet</button>
      </div>
      {resource.status === "loading" && resource.data === null ? <p className="platform-loading" role="status">Loading analytics.</p> : null}
      {resource.data === null && resource.status === "error" ? (
        <div className="platform-notice platform-notice--error" role="alert">
          <p>{resource.message ?? "Analytics could not be loaded."}</p>
          {resource.canRetry ? <button className="platform-button platform-button--secondary" onClick={retry} type="button">Retry analytics</button> : null}
        </div>
      ) : null}
      {resource.data !== null ? (
        <>
          {resource.status === "error" ? (
            <div className="platform-notice platform-notice--error dashboard-page__notice" role="alert">
              <p>{resource.message ?? "Analytics could not be refreshed."}</p>
              {resource.canRetry ? <button className="platform-button platform-button--secondary" onClick={retry} type="button">Retry analytics</button> : null}
            </div>
          ) : null}
          <MetricGrid canBeHidden={false} totals={resource.data.totals} />
          <DailyActivity daily={resource.data.daily} />
          <ProductActivity products={resource.data.products} />
        </>
      ) : null}
    </div>
  );
}

function FleetAnalyticsPage(): JSX.Element {
  const [state, setState] = useAnalyticsUrlState();
  const query = useMemo<AnalyticsUrlState>(() => ({
    days: state.days,
    product: state.product,
    organizationId: null,
  }), [state.days, state.product]);
  const resource = useFleetAnalyticsResource(query);
  const selectedOrganization = state.organizationId === null
    ? null
    : resource.organizations.find((organization) => organization.organizationId === state.organizationId) ?? null;
  const drilldown = useF5OrganizationAnalyticsResource(selectedOrganization?.organizationId ?? null, query);
  const selectOrganization = useCallback((organizationId: string) => {
    setState({ ...state, organizationId });
  }, [setState, state]);
  const returnToFleet = useCallback(() => {
    setState({ ...state, organizationId: null });
  }, [setState, state]);
  const retryFleet = useCallback(() => { void resource.reload(); }, [resource]);
  const loadMore = useCallback(() => { void resource.loadMore(); }, [resource]);

  return (
    <section aria-labelledby="analytics-page-title" className="platform-page dashboard-page analytics-page">
      <header className="dashboard-page__header">
        <div>
          <p className="platform-page__eyebrow">Fulcrum5</p>
          <h1 id="analytics-page-title">Analytics</h1>
        </div>
        <span className="platform-badge" data-tone="active">Fleet analytics</span>
      </header>
      <AnalyticsFilters onChange={setState} state={state} />
      {resource.status === "loading" && resource.data === null ? <p className="platform-loading" role="status">Loading analytics.</p> : null}
      {resource.data === null && resource.status === "error" ? (
        <div className="platform-notice platform-notice--error" role="alert">
          <p>{resource.message ?? "Analytics could not be loaded."}</p>
          {resource.canRetry ? <button className="platform-button platform-button--secondary" onClick={retryFleet} type="button">Retry analytics</button> : null}
        </div>
      ) : null}
      {resource.data !== null && selectedOrganization === null ? (
        <>
          {resource.status === "error" ? (
            <div className="platform-notice platform-notice--error dashboard-page__notice" role="alert">
              <p>{resource.message ?? "Analytics could not be refreshed."}</p>
              {resource.canRetry ? <button className="platform-button platform-button--secondary" onClick={retryFleet} type="button">Retry analytics</button> : null}
            </div>
          ) : null}
          {state.organizationId !== null ? (
            <div className="platform-notice platform-notice--warning analytics-page__selection-notice" role="alert">
              <p>The requested organization is not in the loaded fleet results.</p>
              <button className="platform-button platform-button--quiet" onClick={returnToFleet} type="button">Clear selection</button>
            </div>
          ) : null}
          <MetricGrid canBeHidden={false} totals={resource.data.totals} />
          <FleetOrganizationTable
            hasMore={resource.nextCursor !== null}
            isLoadingMore={resource.isLoadingMore}
            onLoadMore={loadMore}
            onSelect={selectOrganization}
            organizations={resource.organizations}
            state={state}
          />
        </>
      ) : null}
      {selectedOrganization !== null ? (
        <F5OrganizationDrilldown
          onReturnToFleet={returnToFleet}
          organization={selectedOrganization}
          resource={drilldown}
        />
      ) : null}
    </section>
  );
}

export function AnalyticsPage(): JSX.Element {
  const session = usePlatformSession();
  return session.identity?.isF5Admin === true ? <FleetAnalyticsPage /> : <OrganizationAnalyticsPage />;
}
